import {
  AVERAGE_BUFFER_FRAME_TYPE,
  AVERAGE_SAMPLE_FRAME_TYPE,
  BUFFER_OR_SAMPLE_FRAME_TYPE,
  FRAME_SCALE_LINEAR,
  FRAME_SCALE_LOG,
  SUBTRACTED_FRAME_TYPE,
  VIEW_DATASET_SUMMARY,
} from '../../constants/saxs'
import { Col, Grid, Row } from 'react-bootstrap'
import {
  getExtendedMergesByMergeIds as getExtendedMergesByMergeIdsURL,
  getPlottingDataByFrame as getPlottingDataByFrameURL,
  getSaxsDataCollectionsAsBeanByDataCollectionIds as getSaxsDataCollectionsAsBeanByDataCollectionIdsURL,
  getSaxsExtendedSubtractionsByExperimentId,
} from '../../api/ispyb'
import { parseDataCollectionAsBean, parseDataCollections } from '../../helpers/Saxs/SaxsDataCollectionMapper'

import { CSVFrameToDataSeriesParser2 } from '../../helpers/Saxs/SaxsFrameDataSerieMapper'
import DataCollectionListPanel from '../../components/saxs/DataCollection/DataCollectionListPanel'
import FrameListTable from '../../components/saxs/FrameListTable'
import PropTypes from 'prop-types'
import React from 'react'
import SaxsRequester from './helpers/SaxsRequester'
import SaxsSimplePlot from '../../components/saxs/SaxsSimplePlot'
import { axios } from 'config/axios'
import cloneDeep from 'lodash/cloneDeep'
import concat from 'lodash/concat'
import { connect } from 'react-redux'
import filter from 'lodash/filter'
import find from 'lodash/find'
import flatten from 'lodash/flatten'
import flattenDeep from 'lodash/flattenDeep'
import isEqual from 'lodash/isEqual'
import map from 'lodash/map'
import orderBy from 'lodash/orderBy'
import { parseMerges } from '../../helpers/Saxs/SaxsMergesMapper'

/**
 * React component which renders the data reduction page
 */
class SaxsDataReductionContainer extends React.Component {
  render() {
    if (this.props.user.username === null || this.props.user.token === null) {
      return null
    }

    const _this = this
    return (
      <div style={{ paddingBottom: '70px' }}>
        <SaxsRequester
          url={getSaxsExtendedSubtractionsByExperimentId(this.props.user, this.props.proposal, this.props.experimentId)}
          parser={parseDataCollections}
        >
          {dataCollections => {
            const filteredDataCollections =
              filter(
                dataCollections,
                dataCollection => dataCollection.dataCollectionId === _this.props.dataCollectionId
              ) || []
            return (
              <div>
                <DataCollectionListPanel
                  dataCollections={filteredDataCollections}
                  experimentId={this.props.experimentId}
                  proposal={this.props.proposal}
                  sessionId={this.props.sessionId}
                  user={this.props.user}
                  view={VIEW_DATASET_SUMMARY}
                />

                <DataReductionTableAndGraphContainer
                  dataCollectionId={this.props.dataCollectionId}
                  mergeIds={map(filteredDataCollections, subtraction => subtraction.merge_id)}
                  proposal={this.props.proposal}
                  user={this.props.user}
                />
              </div>
            )
          }}
        </SaxsRequester>
      </div>
    )
  }
}

/**
 * React component which renders the lower part of the page: a table of frames and a graph
 */
class DataReductionTableAndGraphContainer extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      frames: [],
      scale: FRAME_SCALE_LOG,
      selectedFrames: [],
    }

    this.onScaleChanged = this.onScaleChanged.bind(this)
    this.onFrameClicked = this.onFrameClicked.bind(this)
  }

  render() {
    return (
      <Grid fluid>
        <Row>
          <Col xs={3} style={{ height: '500px', overflowY: 'scroll' }}>
            <FrameListTable
              frames={this.state.frames}
              selectedFrames={this.state.selectedFrames}
              onRowClicked={this.onFrameClicked}
              scale={this.state.scale}
              onScaleChanged={this.onScaleChanged}
            />
          </Col>
          <Col xs={9}>
            <DataReductionGraphContainer
              frames={this.state.selectedFrames}
              scale={this.state.scale}
              user={this.props.user}
            />
          </Col>
        </Row>
      </Grid>
    )
  }

  /**
   * Get 2 things: all the frames and the information to find out whether they have been discarded or not.
   * The call getExtendedMergesByMergeIds() get the frame list from MergeIds
   * The call getSaxsDataCollectionsAsBeanByDataCollectionIds() get information to find out whether they have been discarded or not
   */
  componentDidMount() {
    Promise.all([this.getExtendedMergesByMergeIds(), this.getSaxsDataCollectionsAsBeanByDataCollectionIds()]).then(
      ([getExtendedMergesByMergeIdsResponse, getSaxsDataCollectionsAsBeanByDataCollectionIdsResponse]) => {
        const merges = parseMerges(getExtendedMergesByMergeIdsResponse.data) // array of merge
        let allFrames = flatten(map(map(merges, 'frameList'), 'frames'))

        const dataCollection = parseDataCollectionAsBean(getSaxsDataCollectionsAsBeanByDataCollectionIdsResponse.data)
        if (dataCollection && dataCollection.subtraction) {
          const subtraction = dataCollection.subtraction

          const bufferFramesNotDiscarded = subtraction.bufferFrameList ? subtraction.bufferFrameList.frames : []
          const sampleFramesNotDiscarded = subtraction.sampleFrameList ? subtraction.sampleFrameList.frames : []

          /** Identify discarded frames */
          for (let i = 0; i < allFrames.length; i++) {
            const frame = allFrames[i]
            frame.discarded = !find(
              concat(bufferFramesNotDiscarded, sampleFramesNotDiscarded),
              f => f.frameId === frame.frameId
            )
            frame.type = BUFFER_OR_SAMPLE_FRAME_TYPE
          }

          allFrames = orderBy(allFrames, ['filePath'], ['asc'])

          const subtractedFrame = {
            filePath: subtraction.subtractedFilePath,
            frameId: subtraction.subtractionId,
            type: SUBTRACTED_FRAME_TYPE,
          }
          allFrames.unshift(subtractedFrame)
          const averageBufferFrame = {
            filePath: subtraction.bufferAverageFilePath,
            frameId: subtraction.subtractionId,
            type: AVERAGE_BUFFER_FRAME_TYPE,
          }
          allFrames.unshift(averageBufferFrame)
          const averageSampleFrame = {
            filePath: subtraction.sampleAverageFilePath,
            frameId: subtraction.subtractionId,
            type: AVERAGE_SAMPLE_FRAME_TYPE,
          }
          allFrames.unshift(averageSampleFrame)

          this.setState({
            frames: allFrames,
            selectedFrames: [subtractedFrame, averageBufferFrame, averageSampleFrame],
          })
        }
      }
    )
  }

  getExtendedMergesByMergeIds() {
    return axios({
      method: 'GET',
      url: getExtendedMergesByMergeIdsURL(this.props.user, this.props.proposal, this.props.mergeIds.join()),
    })
  }

  getSaxsDataCollectionsAsBeanByDataCollectionIds() {
    return axios({
      method: 'GET',
      url: getSaxsDataCollectionsAsBeanByDataCollectionIdsURL(
        this.props.user,
        this.props.proposal,
        this.props.dataCollectionId
      ),
    })
  }

  /**
   * Callback function triggered when the user clicked on the frame in the list
   * @param {int} frameId
   * @param {string} frameType
   */
  onFrameClicked(frameId, frameType) {
    const foundFrame = find(
      this.state.selectedFrames,
      frame => frame && frame.frameId && frame.frameId === frameId && frame.type === frameType
    )

    if (foundFrame) {
      // remove the frame from the selectedFrames array
      this.setState({ selectedFrames: this.state.selectedFrames.filter(frame => frame.frameId !== frameId) })
    } else {
      // add frameId in the selectedFramesIds array
      const newList = cloneDeep(this.state.selectedFrames)
      const frameToAdd = find(
        this.state.frames,
        frame => frame && frame.frameId && frame.frameId === frameId && frame.type === frameType
      )

      newList.push(frameToAdd)
      this.setState({ selectedFrames: newList })
    }
  }

  onScaleChanged(newScale) {
    if (this.state.scale !== newScale) {
      this.setState({ scale: newScale })
    }
  }
}

class DataReductionGraphContainer extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      frames: [],
      dataSeries: [],
      scale: null,
    }

    this.getFrameDataSeries = this.getFrameDataSeries.bind(this)
    this.getPlottingDataByFramePromise = this.getPlottingDataByFramePromise.bind(this)
    this.getScale = this.getScale.bind(this)
  }

  render() {
    return (
      <SaxsSimplePlot
        divId="dataReductionPlot"
        dataSeries={this.state.dataSeries}
        layout={{
          title: 'Scattering curves',
          xaxis: { title: 'q' },
          yaxis: { title: 'Intensity' },
          autosize: true,
        }}
      />
    )
  }

  componentDidUpdate() {
    if (!isEqual(this.state.frames, this.props.frames) || this.state.scale !== this.props.scale) {
      this.getFrameDataSeries(this.props.frames)
    }
  }

  getFrameDataSeries(frames) {
    const promises = []
    frames.forEach(frame => {
      if (frame && frame.type) {
        let frameId = ''
        const averageId = ''
        let subtractedId = ''
        let sampleAverageId = ''
        let bufferAverageId = ''
        const models = ''
        const operation = this.getScale()
        if (frame.type === BUFFER_OR_SAMPLE_FRAME_TYPE) {
          frameId = frame.frameId
        }
        if (frame.type === AVERAGE_BUFFER_FRAME_TYPE) {
          bufferAverageId = frame.frameId
        }
        if (frame.type === AVERAGE_SAMPLE_FRAME_TYPE) {
          sampleAverageId = frame.frameId
        }
        if (frame.type === SUBTRACTED_FRAME_TYPE) {
          subtractedId = frame.frameId
        }

        promises.push(
          this.getPlottingDataByFramePromise(
            frame,
            frameId,
            averageId,
            subtractedId,
            sampleAverageId,
            bufferAverageId,
            models,
            operation
          )
        )
      }
    })

    Promise.all(promises).then(results => {
      if (results) {
        // results = An array which items have the form {response: response, frame: frameObject }
        const dataSeries = []
        results.forEach(result => {
          if (result && result.response && result.response.data) {
            dataSeries.push(CSVFrameToDataSeriesParser2(result.response.data, result.frame))
          }
        })
        this.setState({ dataSeries: flattenDeep(dataSeries), scale: this.props.scale, frames: this.props.frames })
      }
    })
  }

  /** Get the Y-axis scale */
  getScale() {
    const { scale } = this.props
    if (scale) {
      if (scale === FRAME_SCALE_LINEAR) {
        return 'LINEAL'
      }
      if (scale === FRAME_SCALE_LOG) {
        return 'LOG'
      }
    }
    return null
  }

  getPlottingDataByFramePromise(
    frame,
    frameId,
    averageId,
    subtractedId,
    sampleAverageId,
    bufferAverageId,
    models,
    operation
  ) {
    return new Promise((resolve, reject) =>
      axios({
        method: 'GET',
        url: getPlottingDataByFrameURL(
          this.props.user,
          frameId,
          averageId,
          subtractedId,
          sampleAverageId,
          bufferAverageId,
          models,
          operation
        ),
      })
        .then(response => resolve({ response: response, frame: frame }))
        .catch(error => reject({ error: error, frame: frame }))
    )
  }
}

function mapStateToProps(state) {
  return {
    user: state.user,
  }
}

export default connect(mapStateToProps, () => null)(SaxsDataReductionContainer)

SaxsDataReductionContainer.propTypes = {
  /** experiment identifier */
  experimentId: PropTypes.number,
  /** data collection identifier */
  dataCollectionId: PropTypes.number,
  /** proposal name */
  proposal: PropTypes.string,
}
